import { useCallback, useId, useState } from 'react';

import { handleEnterKeyInteraction } from '@/utils/accessibilityHandlers/handleEnterKeyInteraction';

import { TTab,TTabsProps } from './types';
import { createTabsFromConfig, validateUniqueTabTitles } from './utils';

const UiKitTabs = <T extends string>({
  tabConfig,
  defaultTab,
  onTabChange,
  className = '',
  contentClassName = '',
}: TTabsProps<T>) => {

  const tabs = createTabsFromConfig<T>(tabConfig);
  validateUniqueTabTitles(tabs);
  
  const [activeTab, setActiveTab] = useState<T>(defaultTab);
  const baseId = useId();

  const handleTabClick = useCallback((tab: TTab<T>) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (tab.disabled || tab.title === activeTab) return;
    setActiveTab(tab.title);
    onTabChange?.(tab.title);

  }, [onTabChange, activeTab]);

  const handleKeyDown = useCallback((title: T, disabled: boolean) => (e: React.KeyboardEvent) => {
      const handler = handleEnterKeyInteraction(
        () => {
          setActiveTab(title);
          onTabChange?.(title);
        },
        { disabled, preventDefault: true }
      );

      handler(e);
  }, [onTabChange]);

  const getTriggerId = (name: T) => `${baseId}-trigger-${name}`;
  const getPanelId = (name: T) => `${baseId}-panel-${name}`;
  const getTestId = (name: T) => `tab-${name}`;

  return (
    <>
      <ul className={`nav nav-tabs nav-justified nav-justified-word-wrap ${className}`.trim()} role="tablist">
        {tabs.map((tab) => {
          const { title, disabled, label } = tab;
          const isActive = activeTab === title;
          const isDisabled = disabled ?? false;

          return (
            <li
              key={title}
              role="presentation"
              className={`${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`.trim() || undefined}
            >
              <a
                id={getTriggerId(title)}
                href={`#${getPanelId(title)}`}
                role="tab"
                data-toggle="tab"
                aria-controls={getPanelId(title)}
                aria-selected={isActive}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onClick={handleTabClick(tab)}
                onKeyDown={(e) => handleKeyDown(title, isDisabled)(e)}
                className={`${isActive ? 'active-tab' : ''}`}
                data-testid={getTestId(title)}
              >
                {label}
              </a>
            </li>
          );
        })}
      </ul>

      <div className={`tab-content ${contentClassName}`.trim()}>
        {tabs.map((tab) => {
          const { title, content } = tab;
          const isActive = activeTab === title;

          return (
            <div
              key={title}
              id={getPanelId(title)}
              role="tabpanel"
              aria-labelledby={getTriggerId(title)}
              aria-hidden={!isActive}
              className={`tab-panel fade ${isActive ? 'active in' : ''}`.trim()}
            >
              {isActive && content}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default UiKitTabs;